import { ArrowRight, Sparkles, Route } from "lucide-react";
import type { AssistantMessage } from "@/lib/ai-search/assistant";
import type { SearchClickKind } from "@/lib/searchTelemetry";

interface AssistantCardProps {
  assistant: AssistantMessage;
  /** Where a tap should go. The card only reports the destination; the
   * surface showing it records the click and navigates in its own way
   * (each search surface closes / resets itself differently). */
  onNavigate: (href: string, kind: SearchClickKind, position: number) => void;
  /** "mousedown" for surfaces whose input loses focus -- and closes the
   * results -- before a normal click can complete (the header and homepage
   * dropdown); "click" everywhere else. Keyboard activation works in both. */
  trigger?: "click" | "mousedown";
}

/**
 * The AI assistant's contribution to a search: a clarifying question with
 * tappable answers, a short written answer with links to related tools, or
 * a multi-step workflow plan (steps + a guided-pipeline link). Sits above
 * the normal results, clearly labelled as AI so nobody mistakes it for a
 * fixed part of the site.
 *
 * Everything shown is model-written text, so it is rendered only as plain
 * text (React escapes it) -- never as HTML -- and every destination is a
 * link built by our own code from verified tool IDs (see
 * ai-search/assistant.ts), never a URL the model supplied.
 */
export function AssistantCard({ assistant, onNavigate, trigger = "click" }: AssistantCardProps) {
  const bind = (href: string, kind: SearchClickKind, position: number) =>
    trigger === "mousedown"
      ? {
          onMouseDown: (e: React.MouseEvent) => {
            e.preventDefault(); // keep the input focused so the list doesn't close first
            onNavigate(href, kind, position);
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onNavigate(href, kind, position);
            }
          },
        }
      : { onClick: () => onNavigate(href, kind, position) };

  const chip =
    "inline-flex items-center gap-1 rounded-full border border-primary/40 bg-background px-3 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground";

  return (
    <div className="px-4 py-3 border-b border-border/60 bg-primary/5" role="region" aria-label="AI assistant">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary mb-1.5">
        <Sparkles className="h-3 w-3" aria-hidden />
        AI assistant
      </div>
      <p className="text-sm text-foreground leading-relaxed">{assistant.text}</p>

      {assistant.kind === "clarify" && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {assistant.options.map((o, i) => (
            <button key={`${o.label}-${i}`} type="button" className={chip} {...bind(o.href, "assistant_option", i)}>
              {o.label}
            </button>
          ))}
        </div>
      )}

      {assistant.kind === "answer" && assistant.tools.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {assistant.tools.map((t, i) => (
            <button key={t.id} type="button" className={chip} {...bind(t.href, "assistant_tool", i)}>
              {t.name}
              <ArrowRight className="h-3 w-3" aria-hidden />
            </button>
          ))}
        </div>
      )}

      {assistant.kind === "workflow" && (
        <>
          <ol className="text-sm mt-2 mb-2.5 pl-5 list-decimal space-y-0.5 text-foreground">
            {assistant.steps.map((s) => (
              <li key={s.id}>{s.name}</li>
            ))}
          </ol>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            {...bind(assistant.href, "assistant_workflow", 0)}
          >
            <Route className="h-3.5 w-3.5" aria-hidden />
            Guide me through it &rarr;
          </button>
        </>
      )}
    </div>
  );
}
